export type ClientLoadPhase =
  | "runtime-mounted"
  | "connection-ready"
  | "projects-ready"
  | "project-memory-ready"
  | "project-restored"
  | "candidate-jobs-ready"
  | "comparison-visible";

export type ClientLoadDetail = {
  viewKey?: string | null;
  projectPath?: string;
  rows?: number;
};

export type ClientLoadTimelineEntry =
  | {
      kind: "phase";
      name: ClientLoadPhase;
      atMs: number;
      detail: { rows?: number };
    }
  | {
      kind: "graphql";
      name: string;
      stage: "start" | "first_result" | "complete" | "error";
      atMs: number;
      elapsedMs: number;
    };

const PREFIX = "deploycompare:load:";
const DATA_ATTRIBUTE = "data-deploycompare-load-timeline";
const MAX_ENTRIES = 128;
const timeline: ClientLoadTimelineEntry[] = [];

function nowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function append(entry: ClientLoadTimelineEntry): void {
  timeline.push(entry);
  if (timeline.length > MAX_ENTRIES) timeline.splice(0, timeline.length - MAX_ENTRIES);
  // A DOM-owned export makes the waterfall inspectable in staging automation
  // without DevTools privileges. It is diagnostic-only and contains operation
  // names/timings, never variables, project paths, tokens, or response data.
  try {
    document.documentElement.setAttribute(DATA_ATTRIBUTE, JSON.stringify(timeline));
  } catch {
    // Diagnostics must never interfere with the view path.
  }
}

/**
 * Record browser-local read-path boundaries without putting telemetry or
 * network writes on the critical path. Repeated names are intentional: the
 * Performance timeline retains each provider/project transition separately.
 */
export function markClientLoadPhase(phase: ClientLoadPhase, detail: ClientLoadDetail = {}): void {
  if (typeof performance === "undefined" || typeof performance.mark !== "function") return;
  try {
    performance.mark(`${PREFIX}${phase}`, { detail });
  } catch {
    // Older browsers implement mark(name) but not PerformanceMarkOptions.
    try {
      performance.mark(`${PREFIX}${phase}`);
    } catch {
      // Diagnostics must never interfere with the view path.
    }
  }
  append({
    kind: "phase",
    name: phase,
    atMs: nowMs(),
    detail: detail.rows === undefined ? {} : { rows: detail.rows },
  });
}

export function markClientGraphqlOperation(
  name: string,
  stage: "start" | "first_result" | "complete" | "error",
  startedAtMs: number
): void {
  const atMs = nowMs();
  append({
    kind: "graphql",
    name: name || "anonymous",
    stage,
    atMs,
    elapsedMs: Math.max(0, atMs - startedAtMs),
  });
}

export function clientLoadTimelineNow(): number {
  return nowMs();
}

export function readClientLoadTimeline(): ClientLoadTimelineEntry[] {
  return timeline.map((entry) => ({ ...entry }));
}

export function clientLoadMarkName(phase: ClientLoadPhase): string {
  return `${PREFIX}${phase}`;
}
